import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';

export default function CampaignForm({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    advertiser_id: '',
    start_date: '',
    end_date: '',
    play_target: '',
    budget: '',
    is_active: true,
    notes: '',
  });

  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAdvertisers();
    if (isEdit) {
      fetchCampaign();
    }
  }, [id]);

  const fetchAdvertisers = async () => {
    try {
      const { data } = await api.get('/api/advertisers');
      setAdvertisers(data);
    } catch (err) {
      console.error('[CampaignForm] Failed to fetch advertisers:', err);
    }
  };

  const fetchCampaign = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/campaigns/${id}`);
      setFormData({
        name: data.name || '',
        advertiser_id: data.advertiser_id || '',
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        play_target: data.play_target || '',
        budget: data.budget || '',
        is_active: data.is_active ?? true,
        notes: data.notes || '',
      });
    } catch (err) {
      console.error('[CampaignForm] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Campaign name is required';
    if (!formData.advertiser_id) newErrors.advertiser_id = 'Advertiser is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        play_target: formData.play_target ? parseInt(formData.play_target, 10) : null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
      };

      if (isEdit) {
        await api.put(`/api/campaigns/${id}`, payload);
      } else {
        await api.post('/api/campaigns', payload);
      }
      navigate('/advertising/campaigns');
    } catch (err) {
      console.error('[CampaignForm] Save failed:', err);
      if (err.response?.data?.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: 'Failed to save campaign.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading campaign...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {isEdit ? 'Edit Campaign' : 'Create Campaign'}
        </h1>
        <Link to="/advertising/campaigns" className="btn btn-secondary btn-sm">
          Back to Campaigns
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Campaign Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`input ${errors.name ? 'border-error-500' : ''}`}
              placeholder="Campaign name"
            />
            {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Advertiser *</label>
            <select
              name="advertiser_id"
              value={formData.advertiser_id}
              onChange={handleChange}
              className={`input ${errors.advertiser_id ? 'border-error-500' : ''}`}
            >
              <option value="">Select advertiser</option>
              {advertisers.map((adv) => (
                <option key={adv.id} value={adv.id}>{adv.name}</option>
              ))}
            </select>
            {errors.advertiser_id && <p className="text-xs text-error-400 mt-1">{errors.advertiser_id}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Start Date</label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">End Date</label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleChange}
              className="input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Play Target</label>
            <input
              type="number"
              name="play_target"
              value={formData.play_target}
              onChange={handleChange}
              className="input"
              placeholder="Number of plays"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Budget ($)</label>
            <input
              type="number"
              name="budget"
              value={formData.budget}
              onChange={handleChange}
              className="input"
              placeholder="Total budget"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-dark-300 mb-1">Notes</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="input min-h-[60px]"
            placeholder="Internal notes..."
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
          />
          <label htmlFor="is_active" className="text-sm text-dark-300">Active</label>
        </div>

        <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
          <Link to="/advertising/campaigns" className="btn btn-secondary">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Saving...' : isEdit ? 'Update Campaign' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>
  );
}
